import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Sprout, Flower, Leaf, Sun, Droplets, BookOpen, Microscope, Calendar } from 'lucide-react';

const HorticultureBotany: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Royal Botanical Institute";
    
    return (
        <div className="min-h-screen bg-[#fcfdfa] font-serif text-[#1a2e1a] selection:bg-[#d4e6d4] selection:text-[#1a2e1a] border-t-8 border-[#2d5a27]">
            {/* Top Bar Navigation */}
            <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-[#e8f0e8]">
                <div className="max-w-7xl mx-auto px-8 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Leaf size={24} className="text-[#2d5a27]" strokeWidth={1.5} />
                        <div>
                             <span className="block font-bold text-lg tracking-tight leading-none text-[#1a2e1a]">{schoolName}</span>
                             <span className="block font-sans text-[9px] font-bold uppercase tracking-[0.2em] text-[#6d8a67]">Research & Conservation</span>
                        </div>
                    </div>

                    <div className="hidden md:flex gap-10 font-sans text-xs font-bold text-[#5c7a56] uppercase tracking-[0.15em]">
                        <a href="#" className="hover:text-[#2d5a27] transition-colors">Gardens</a>
                        <a href="#" className="hover:text-[#2d5a27] transition-colors">Education</a>
                        <a href="#" className="hover:text-[#2d5a27] transition-colors">Science</a>
                        <a href="#" className="hover:text-[#2d5a27] transition-colors">Membership</a>
                    </div>
                    
                    <Button variant="outline" className="rounded-full border-[#2d5a27] text-[#2d5a27] hover:bg-[#2d5a27] hover:text-white font-sans text-xs font-bold uppercase tracking-widest h-9 px-6 transition-colors">
                        Visit
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative py-24 px-8 overflow-hidden">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-6 relative z-10">
                        <div className="inline-flex items-center gap-2 mb-6 text-[#2d5a27] bg-[#f0f7f0] px-4 py-2 rounded-full">
                            <Sprout size={16} />
                            <span className="font-sans text-xs font-bold uppercase tracking-widest">Seeds of Knowledge</span>
                        </div>
                        
                        <h1 className="text-5xl md:text-7xl font-light text-[#1a2e1a] mb-8 leading-tight">
                            Cultivating a <br/>
                            Greener <span className="italic font-normal text-[#4a7a44]">World.</span>
                        </h1>
                        
                        <p className="font-sans text-[#5c7a56] text-lg leading-relaxed max-w-lg mb-10">
                            Dedicated to the study, conservation, and display of the world's plant diversity. Join our diploma programs in horticulture and landscape design.
                        </p>
                        
                        <div className="flex flex-wrap gap-4">
                            <Button className="rounded-full bg-[#2d5a27] hover:bg-[#1e3d1a] text-white px-8 h-12 font-sans text-xs font-bold uppercase tracking-widest shadow-lg shadow-green-900/10">
                                Apply Now
                            </Button>
                            <Button variant="ghost" className="rounded-full text-[#2d5a27] hover:bg-[#f0f7f0] px-8 h-12 font-sans text-xs font-bold uppercase tracking-widest">
                                Read Prospectus
                            </Button>
                        </div>
                    </div>
                
                    {/* Illustration / Image Area */}
                    <div className="lg:col-span-6 relative h-[500px] border border-[#e8f0e8] bg-white rounded-t-[1000px] overflow-hidden shadow-xl">
                         <img 
                            src="https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?q=80&w=2070&auto=format&fit=crop" 
                            alt="Botanical Garden" 
                            className="absolute inset-0 w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-1000"
                         />
                         <div className="absolute inset-0 border-[20px] border-white/80 rounded-t-[1000px] pointer-events-none"></div>
                         
                         {/* Card Overlay */}
                         <div className="absolute bottom-8 right-8 bg-white/95 backdrop-blur p-6 rounded-sm shadow-sm max-w-[200px]">
                             <p className="font-serif italic text-sm text-[#2d5a27] mb-2">Monstera deliciosa</p>
                             <div className="h-px w-full bg-[#e8f0e8] mb-2"></div>
                             <p className="font-sans text-[10px] text-[#5c7a56] leading-snug">
                                 Native to tropical forests of southern Mexico, south to Panama.
                             </p>
                         </div>
                    </div>
                </div>
            </header>

            {/* Programs Section */}
            <section className="py-24 px-8 bg-[#f5f9f5]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-light text-[#1a2e1a] mb-4">Educational Programs</h2>
                        <p className="font-sans text-sm text-[#5c7a56] max-w-2xl mx-auto">
                            From amateur gardening courses to PhD research opportunities, discover our wide range of academic offerings.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: Flower, title: "Horticulture", subtitle: "Practice & Theory" },
                            { icon: Microscope, title: "Plant Science", subtitle: "Botany & Genetics" },
                            { icon: Sun, title: "Conservation", subtitle: "Sustainability & Ecology" },
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-10 rounded-xl shadow-sm hover:shadow-md transition-shadow hover:translate-y-[-4px] duration-300 border border-[#e8f0e8] text-center">
                                <div className="w-16 h-16 mx-auto bg-[#f0f7f0] rounded-full flex items-center justify-center text-[#2d5a27] mb-6">
                                    <item.icon size={28} strokeWidth={1} />
                                </div>
                                <h3 className="text-xl font-normal text-[#1a2e1a] mb-2">{item.title}</h3>
                                <p className="font-sans text-xs font-bold uppercase tracking-widest text-[#6d8a67]">{item.subtitle}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#1a2e1a] text-white py-20 px-8">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-12">
                     <div className="max-w-sm">
                         <div className="flex items-center gap-3 mb-6">
                            <Leaf size={20} className="text-[#a3c9a0]" />
                            <span className="font-bold text-lg">{schoolName}</span>
                         </div>
                         <p className="font-sans text-xs text-[#a3c9a0] leading-relaxed">
                             Our mission is to understand and conserve plants and fungi, for the well-being of people and the future of all life on Earth.
                         </p>
                     </div>
                     
                     <div className="flex gap-16 font-sans text-xs font-bold uppercase tracking-widest text-[#a3c9a0]">
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white transition-colors">Herbarium</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Library</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Seed Bank</a></li>
                         </ul>
                         <ul className="space-y-4">
                             <li><a href="#" className="hover:text-white transition-colors">Volunteer</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Donate</a></li>
                             <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-[#2d5a27] text-center md:text-left text-[10px] font-sans text-[#5c7a56]">
                     © 2026 {schoolName}. Registered Charity No. 123456.
                 </div>
            </footer>
        </div>
    );
};

export default HorticultureBotany;
